import CommonModal from 'components/common/CommonModal';
import DefaultButton from 'components/common/DefaultButton';
import DefaultImageView from 'components/common/DefaultImageView';
import LabelDocument from 'components/common/LabelDocument';
import LabelInput from 'components/common/LabelInput';
import LabelRadio from 'components/common/LabelRadio';
import LabelTextarea from 'components/common/LabelTextarea';
import {healthStatusOption, isNormalAnnouncementOption} from 'data/option';
import {createStatus, patchStatus} from 'function/api';
import {
  ICreateStatusBody,
  IPatchStatusBody,
  IStatus,
} from 'interfaces/interface';
import moment from 'moment-timezone';
import React, {useEffect, useState} from 'react';
import {useRecoilValue} from 'recoil';
import styled from 'styled-components';
import {userInfoState} from 'utils/Atoms';
import {minmax} from 'utils/minmax';

/**
 * 공지사항 생성 데이터 인터페이스
 * @property {string} statusName - 상태명
 * @property {string} description - 설명
 * @property {string} startDate - 시작 날짜
 * @property {string} endDate - 종료 날짜
 * @property {boolean} isClosed - 종료 여부
 * @property {File[]} files - 파일 목록
 * @property {string} title - 제목
 */
interface IMakeData {
  statusName: string;
  description: string;
  startDate: string;
  endDate: string;
  isClosed: boolean;
  files: File[];
  title: string;
}

/**
 * 공지사항 등록 모달 컴포넌트의 Props 타입
 * @property {() => void} closeModal - 모달 닫기 함수
 * @property {null | IStatus} parentData - 부모 데이터
 * @property {React.Dispatch<React.SetStateAction<{}>>} setErrorModal - 에러 모달 설정 함수
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsLoading - 로딩 상태 설정 함수
 * @property {boolean} [isModify] - 수정 모드 여부
 * @property {boolean} isNormalNotice - 일반 공지 여부
 */
type Props = {
  closeModal: () => void;
  parentData: null | IStatus;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isModify?: boolean;
  isNormalNotice: boolean;
};

/**
 * 공지사항 등록 모달 컴포넌트
 * 공지사항을 등록하거나 수정하는 모달 컴포넌트입니다.
 * @param {Props} props - 공지사항 등록 모달 컴포넌트의 props
 * @returns {JSX.Element} 공지사항 등록 모달 컴포넌트
 */
const RegisterNoticeModal = ({
  closeModal,
  parentData,
  setErrorModal,
  setIsLoading,
  isModify,
  isNormalNotice,
}: Props) => {
  const userInfo = useRecoilValue(userInfoState);

  const initMakeData = {
    statusName: isNormalNotice ? 'isNormalAnnouncement' : 'ApplyingHotfix',
    description: '',
    startDate: moment().format('YYYY-MM-DD HH:mm:ss'),
    endDate: moment().format('YYYY-MM-DD HH:mm:ss'),
    isClosed: true,
    files: [] as any,
    title: '',
  };
  const [makeData, setMakeData] = useState<IMakeData>(initMakeData);
  const [completeModal, setCompleteModal] = useState(false);

  useEffect(() => {
    if (parentData) {
      let newMakeData = {...initMakeData};
      if (parentData.healthStatus)
        newMakeData.statusName = parentData.healthStatus;
      if (parentData.description)
        newMakeData.description = parentData.description;
      if (parentData.title) newMakeData.title = parentData.title;
      setMakeData(newMakeData);
    }
    return () => {
      setMakeData(initMakeData);
    };
  }, []);

  const handleClickRegister = async () => {
    if (isModify && parentData) {
      let data: IPatchStatusBody = {
        issuedAt: makeData.startDate,
        estimatedCompletionTime: makeData.endDate,
        description: makeData.description,
        isClosed: makeData.isClosed,
        statusUUID: parentData.uuid,
      };

      if (makeData.title) data.title = makeData.title;

      const result = await patchStatus({
        data,
        setErrorModal,
        setIsLoading,
        userInfo,
      });
      if (result) setCompleteModal(true);
    } else {
      let data: ICreateStatusBody = {
        description: makeData.description,
        issuedAt: makeData.startDate,
      };

      if (makeData.statusName === 'isNormalAnnouncement') {
        data.isNormalAnnouncement = true;
        if (makeData.files) data.files = makeData.files;
        data.title = makeData.title;
      } else {
        data.healthStatus = makeData.statusName;
        data.estimatedCompletionTime = makeData.endDate;
      }

      if (parentData) {
        data.parentStatusUUID = parentData.uuid;
      }

      const result = await createStatus({
        data,
        setErrorModal,
        setIsLoading,
        userInfo,
      });
      if (result) setCompleteModal(true);
    }
  };

  return (
    <>
      <CommonModal closeModal={closeModal}>
        <Container>
          <DefaultImageView
            source="images/common/close.png"
            style={{
              alignSelf: 'flex-end',
              width: minmax(30),
              marginBottom: minmax(20),
              cursor: 'pointer',
            }}
            onClick={closeModal}
          />
          <Field>
            {isModify ? (
              <LabelRadio
                labelText="종료여부"
                value={makeData.isClosed}
                setValue={(value: boolean) =>
                  setMakeData({...makeData, isClosed: value})
                }
                options={[
                  {text: '예', value: true},
                  {text: '아니오', value: false},
                ]}
                radioListStyle={{alignItems: 'center'}}
              />
            ) : (
              <LabelRadio
                labelText="상태명"
                value={makeData.statusName}
                setValue={(value: string) =>
                  setMakeData({...makeData, statusName: value})
                }
                options={
                  isNormalNotice
                    ? isNormalAnnouncementOption
                    : healthStatusOption
                }
                radioListStyle={{alignItems: 'center'}}
              />
            )}

            {isNormalNotice ? (
              <LabelInput
                labelText="제목"
                type="text"
                value={makeData.title}
                setValue={(value: string) =>
                  setMakeData({...makeData, title: value})
                }
              />
            ) : null}
            <LabelTextarea
              labelText="설명"
              value={makeData.description}
              setValue={(value: string) =>
                setMakeData({...makeData, description: value})
              }
              textAreaStyle={{height: minmax(100)}}
            />
            <LabelInput
              labelText="이슈생성시간"
              type="text"
              value={makeData.startDate}
              setValue={(value: string) =>
                setMakeData({...makeData, startDate: value})
              }
            />
            <LabelInput
              labelText="종료예정시간"
              type="text"
              value={makeData.endDate}
              setValue={(value: string) =>
                setMakeData({...makeData, endDate: value})
              }
            />
            {/* TODO: 추후 수정 가능하게 되면 !isModify 삭제 */}
            {!isModify && makeData.statusName === 'isNormalAnnouncement' ? (
              <LabelDocument
                labelText={'첨부 파일'}
                list={makeData.files}
                inputAccept="*"
                setList={(files: File[]) => setMakeData({...makeData, files})}
                listMaxLength={5}
              />
            ) : null}
          </Field>
          <DefaultButton
            text={isModify ? 'modify' : 'register'}
            buttonStyle={{width: '100%'}}
            textStyle={{fontSize: minmax(25)}}
            actived={/* errorList.length ? 0 : */ 1}
            onClick={handleClickRegister}
          />
        </Container>
      </CommonModal>

      {completeModal && (
        <CommonModal
          title={isModify ? '수정 성공!' : '생성 성공!'}
          detail={isModify ? '수정을 완료했습니다.' : '생성을 완료했습니다.'}
          firstButtonText="confirm"
          firstButtonClick={() => {
            setCompleteModal(false);
            closeModal();
            window.location.reload();
          }}
        />
      )}
    </>
  );
};

export default RegisterNoticeModal;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
`;

const Field = styled.div`
  width: 100%;
  margin-bottom: ${minmax(40)};
  flex: 1;
  display: flex;
  flex-direction: column;
  row-gap: ${minmax(20)};
`;
